import React, { memo, useMemo } from "react";
import GraphNotice, { GraphNoticeType } from "./GraphNotice";
import GraphLoading from "./GraphLoading";

interface VisualizationContentProps {
  hasError: boolean;
  hasNoData: boolean;
  /** Records exist but total trade value is 0. */
  hasZeroTrade?: boolean;
  loading: boolean;
  noticeType?: GraphNoticeType;
  /** Error-specific notice type (e.g. RateLimit). Takes precedence over
   *  noticeType when hasError is true. */
  errorNoticeType?: GraphNoticeType;
  children: React.ReactNode;
}

const VisualizationContent = memo(
  ({
    hasError,
    hasNoData,
    hasZeroTrade,
    loading,
    noticeType,
    errorNoticeType,
    children,
  }: VisualizationContentProps) => {
    const content = useMemo(() => {
      if (hasError) {
        return (
          <GraphNotice
            graphNoticeType={
              errorNoticeType ?? noticeType ?? GraphNoticeType.Error
            }
          />
        );
      }
      if (hasNoData) {
        return (
          <GraphNotice
            graphNoticeType={noticeType ?? GraphNoticeType.NoData}
          />
        );
      }
      if (hasZeroTrade) {
        return (
          <GraphNotice
            graphNoticeType={GraphNoticeType.ZeroTrade}
          />
        );
      }
      return children;
    }, [hasError, hasNoData, hasZeroTrade, noticeType, errorNoticeType, children]);

    return (
      <>
        {content}
        {loading && !hasError && <GraphLoading />}
      </>
    );
  },
);

VisualizationContent.displayName = "VisualizationContent";

export default VisualizationContent;
